/**
 * Audit UI Selectors
 *
 * Memoized selectors for accessing audit UI state.
 * Only selects pure UI state - derived values from RTK Query
 * should be computed via hooks, not selectors.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { FeatureSelectorWithFallback } from '@store/storeTypes';

import type { AuditUiState } from './auditUiSlice';
import type { DismissalsState } from './dismissalsSlice';

// ============================================================================
// Empty State Fallback
// ============================================================================

const emptyState: AuditUiState = {
  highlightedSectionId: null,
  sidebarMode: 'chat',
};

// ============================================================================
// Base Selector with Fallback
// ============================================================================

/**
 * Selects the audit UI feature state with fallback
 * Uses any type for state to handle dynamic feature slice registration
 */
export const selectAuditUiState: FeatureSelectorWithFallback<AuditUiState, typeof emptyState> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
) => state['jotaudit/ui'] || emptyState;

// ============================================================================
// UI State Selectors
// ============================================================================

/**
 * Selects the highlighted section ID
 */
export const selectHighlightedSectionId = createSelector(
  [selectAuditUiState],
  state => state.highlightedSectionId,
);

/**
 * Selects the sidebar mode
 */
export const selectAuditSidebarMode = createSelector(
  [selectAuditUiState],
  state => state.sidebarMode,
);

// ============================================================================
// Dismissals State Selectors
// ============================================================================

/**
 * Empty dismissals state fallback
 */
const emptyDismissalsState: DismissalsState = {
  dismissedCardIds: [],
};

/**
 * Selects the dismissals feature state with fallback
 * Uses any type for state to handle dynamic feature slice registration
 */
export const selectDismissalsState: FeatureSelectorWithFallback<
  DismissalsState,
  typeof emptyDismissalsState
> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
) => state['jotaudit/dismissals'] || emptyDismissalsState;

/**
 * Selects the array of dismissed card IDs
 */
export const selectDismissedCardIds = createSelector(
  [selectDismissalsState],
  dismissals => dismissals.dismissedCardIds,
);

/**
 * Factory selector to check if a specific card is dismissed
 */
export const makeSelectIsCardDismissed = (cardId: string) =>
  createSelector([selectDismissedCardIds], dismissedIds =>
    dismissedIds.includes(cardId),
  );
